/**
 * Deferred-commit undo for archives / soft deletes.
 *
 * The UI applies the change at once; the server write waits out the undo
 * window. "Deshacer" cancels the pending write instead of calling a restore
 * RPC: requirements have none (their SELECT policy hides archived rows, so a
 * client UPDATE can't reach them), and restore_property doesn't put back what
 * archive_property unpublished / unshared.
 *
 * While a write is pending the server still has the old row, so the list
 * fetchers pass their results through applyPendingOverlay() — otherwise a
 * refetch (e.g. the list remounting after an archive from the detail page)
 * would bring the row back.
 */
export type PendingKind = 'properties' | 'requirements' | 'contacts';

export interface UndoableAction {
  kind: PendingKind;
  id: string;
  /** Fields to overlay on a refetched row; null drops the row instead. */
  patch: Record<string, unknown> | null;
  /** The server write. Throw on failure. */
  commit: () => Promise<void>;
  /** Put the UI back. Runs on undo, and when commit fails. */
  revert: () => void;
  onError?: (err: unknown) => void;
}

export const UNDO_WINDOW_MS = 7000;

interface Entry {
  action: UndoableAction;
  timer: ReturnType<typeof setTimeout>;
  committing: boolean;
}

const pending = new Map<number, Entry>();
let seq = 0;

export function scheduleUndoable(action: UndoableAction, windowMs = UNDO_WINDOW_MS): number {
  const key = ++seq;
  pending.set(key, { action, timer: setTimeout(() => { void commitPending(key); }, windowMs), committing: false });
  return key;
}

/** Cancels the pending write and reverts the UI. False once the write has started. */
export function undoPending(key: number): boolean {
  const entry = pending.get(key);
  if (!entry || entry.committing) return false;
  clearTimeout(entry.timer);
  pending.delete(key);
  entry.action.revert();
  return true;
}

export async function commitPending(key: number): Promise<void> {
  const entry = pending.get(key);
  if (!entry || entry.committing) return;
  clearTimeout(entry.timer);
  // Stays in the map (overlay still applies) until the server has the change.
  entry.committing = true;
  try {
    await entry.action.commit();
  } catch (err) {
    entry.action.revert();
    entry.action.onError?.(err);
  } finally {
    pending.delete(key);
  }
}

/** Best effort on tab close: start every pending write now. */
export function flushAllPending(): void {
  for (const key of [...pending.keys()]) void commitPending(key);
}

/** Most recent action that can still be undone (for Cmd/Ctrl+Z). */
export function latestUndoableKey(): number | undefined {
  let latest: number | undefined;
  for (const [key, entry] of pending) if (!entry.committing) latest = key;
  return latest;
}

export function applyPendingOverlay<T extends { id: unknown }>(kind: PendingKind, rows: T[]): T[] {
  const byId = new Map<string, Record<string, unknown> | null>();
  for (const { action } of pending.values()) if (action.kind === kind) byId.set(action.id, action.patch);
  if (byId.size === 0) return rows;
  const out: T[] = [];
  for (const row of rows) {
    const id = String(row.id);
    if (!byId.has(id)) { out.push(row); continue; }
    const patch = byId.get(id);
    if (patch) out.push({ ...row, ...patch });
  }
  return out;
}
